import { fetchJwPlayers } from "../actions/global";

const JW_LIBRARY_PUBLIC = "public";
const JW_LIBRARY_PROTECTED = "protected";
const JW_LIBRARY_DRM_LEGACY = "drm";
const SCRIPT_SELECTOR = "script[data-jw-library]";
const LEGACY_PLAYER_KEYS = {
  [JW_LIBRARY_PUBLIC]: "cV8uklFn",
  [JW_LIBRARY_PROTECTED]: "ETYXQEY8",
};

let activeMode = null;
let activePromise = null;

const removeManagedScripts = () => {
  document.querySelectorAll(SCRIPT_SELECTOR).forEach((script) => script.remove());
};

const resetJwPlayerGlobal = () => {
  try {
    delete window.jwplayer;
  } catch (error) {
    window.jwplayer = undefined;
  }
};

const getLibraryMode = (item, index) => {
  if (item?.mode === JW_LIBRARY_PUBLIC) {
    return JW_LIBRARY_PUBLIC;
  }

  if (item?.mode === JW_LIBRARY_PROTECTED || item?.mode === JW_LIBRARY_DRM_LEGACY) {
    return JW_LIBRARY_PROTECTED;
  }

  return index === 0 ? JW_LIBRARY_PUBLIC : JW_LIBRARY_PROTECTED;
};

const buildLegacyLibraryUrl = (item, index) => {
  const mode = getLibraryMode(item, index);
  const playerKey = item?.player || LEGACY_PLAYER_KEYS[mode];

  if (!playerKey || !item?.signature || !item?.expiration) {
    return null;
  }

  return `https://cdn.jwplayer.com/libraries/${playerKey}.js?sig=${item.signature}&exp=${item.expiration}`;
};

const resolveLibraryUrl = (libraries, requestedMode) => {
  const matchingLibrary = libraries.find(
    (item, index) => getLibraryMode(item, index) === requestedMode
  );

  if (!matchingLibrary) {
    throw new Error(`JWPlayer library config missing for mode: ${requestedMode}`);
  }

  return matchingLibrary.url || buildLegacyLibraryUrl(matchingLibrary, libraries.indexOf(matchingLibrary));
};

const appendLibraryScript = (src, mode) =>
  new Promise((resolve, reject) => {
    if (!src) {
      reject(new Error(`JWPlayer library URL missing for mode: ${mode}`));
      return;
    }

    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.crossOrigin = "anonymous";
    script.dataset.jwLibrary = "true";
    script.dataset.jwMode = mode;

    script.onload = () => resolve(window.jwplayer);
    script.onerror = () =>
      reject(new Error(`Failed to load JWPlayer library for mode: ${mode}`));

    document.body.appendChild(script);
  });

export const ensureJwPlayerLibrary = async ({
  dispatch,
  mode,
  drm = false,
}) => {
  const requestedMode =
    mode === JW_LIBRARY_PUBLIC || mode === JW_LIBRARY_PROTECTED
      ? mode
      : drm
        ? JW_LIBRARY_PROTECTED
        : JW_LIBRARY_PUBLIC;

  if (activeMode === requestedMode && window.jwplayer) {
    return window.jwplayer;
  }

  if (activeMode === requestedMode && activePromise) {
    return activePromise;
  }

  activeMode = requestedMode;
  activePromise = (async () => {
    removeManagedScripts();
    resetJwPlayerGlobal();

    const libraries = await dispatch(fetchJwPlayers());
    const libraryUrl = resolveLibraryUrl(libraries, requestedMode);

    return appendLibraryScript(libraryUrl, requestedMode);
  })();

  try {
    const jwPlayer = await activePromise;
    activePromise = null;
    return jwPlayer;
  } catch (error) {
    activeMode = null;
    activePromise = null;
    throw error;
  }
};

export const resetJwPlayerLibraryForTests = () => {
  activeMode = null;
  activePromise = null;
  removeManagedScripts();
  resetJwPlayerGlobal();
};
